import { GetStaticPropsContext } from 'next';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs } from '~/components/breadcrumbs';
import { CohortJourneySection } from '~/features/enterprise/landing-pages/components/cohort-journey-section';
import { EnterpriseLandingFeatureCardsSection } from '~/features/enterprise/landing-pages/components/enterprise-landing-feature-cards-section';
import { EnterpriseVideoHero } from '~/features/enterprise/landing-pages/components/enterprise-video-hero';
import { FooterBannerSection } from '~/features/enterprise/landing-pages/components/footer-banner-section';
import { ImpactSection } from '~/features/enterprise/landing-pages/components/impact-section';
import { SkillsIntelligenceSection } from '~/features/enterprise/landing-pages/components/skills-intelligence-section';
import cohortsConfig from '~/features/enterprise/landing-pages/configs/cohorts.json';
import { OutcomesIcon } from '~/features/enterprise/landing-pages/icons';
import { EnterpriseCohortsLandingPageConfig } from '~/features/enterprise/landing-pages/models/enterprise-platform-landing-page-config';
import { getEnterpriseLandingAnalyticsLabels } from '~/features/enterprise/landing-pages/utils/get-enterprise-landing-analytics-labels';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import {
  LogosCarousel,
  StaticLogosCarouselProps,
  transformStaticLogosCarouselProps,
} from '~/features/page-template/templates/enterprise-overview/components/logos-carousel';

type CohortsLandingPageProps = {
  config: EnterpriseCohortsLandingPageConfig;
  page: Page;
  staticLogosCarouselProps: StaticLogosCarouselProps;
};

export default function EnterpriseCohortsLandingPage({
  config,
  page,
  staticLogosCarouselProps,
}: CohortsLandingPageProps) {
  const { t } = useTranslation();
  const { hero, slug } = config;
  const analyticsLabels = getEnterpriseLandingAnalyticsLabels(slug);

  const breadcrumbs = config.breadcrumbs.map((bc) => ({
    label: t(`enterprise.${slug}.breadcrumbs.${bc.id}`),
    href: bc.href,
  }));

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <Breadcrumbs items={breadcrumbs} />
        <EnterpriseVideoHero analyticsLabels={analyticsLabels} hero={hero} slug={slug} />
        <LogosCarousel {...staticLogosCarouselProps} />
        {config.skillsIntelligence && <SkillsIntelligenceSection config={config.skillsIntelligence} slug={slug} />}
        {config.impact && <ImpactSection config={config.impact} slug={slug} />}
        {config.benefits && (
          <EnterpriseLandingFeatureCardsSection
            slug={slug}
            config={{
              ...config.benefits,
              headerIcon: <OutcomesIcon color='grey.500' height='18px' width='18px' />,
            }}
          />
        )}
        {config.cohortJourney && <CohortJourneySection config={config.cohortJourney} slug={slug} />}
        <FooterBannerSection footerBanner={config.footerBanner} slug={slug} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  if (locale !== DEFAULT_LANGUAGE) {
    return { notFound: true };
  }

  const config = cohortsConfig as EnterpriseCohortsLandingPageConfig;

  const [page, staticLogosCarouselProps] = await Promise.all([
    getPage('/enterprise/overview/[slug]'),
    transformStaticLogosCarouselProps(),
  ]);

  if (!page) {
    return { notFound: true };
  }

  return {
    props: {
      page,
      config,
      staticLogosCarouselProps,
    },
  };
}
